import React, { useState, useEffect, useRef } from 'react';
import { Input, Button } from 'antd';
import {
  ThunderboltFilled,
  ThunderboltOutlined,
  CalendarOutlined,
  ArrowRightOutlined,
  CheckCircleFilled,
  LoadingOutlined,
  AudioOutlined,
  AudioMutedOutlined,
} from '@ant-design/icons';
import contractsModule from './contracts';
import useSpeechInput from './useSpeechInput';
import './aiCrew.css';

const contracts = contractsModule.default || contractsModule;
const { LIMITS } = contracts;

const MAX_PROMPT_LENGTH = LIMITS?.MAX_PROMPT_CHARS || 500;

const REASONING_STAGES = [
  'Interpreting shift request & natural language parameters',
  'Matching the event, task & pay scale from past crews',
  'Checking workforce schedule conflicts & ranking roster',
];

/**
 * PromptPanel - Captures crew intent via one text box (or manual dropdowns if LLM unavailable).
 * Replaced by an Agent Reasoning HUD during analysis.
 */
export default function PromptPanel({
  step,
  prompt,
  setPrompt,
  onBuildCrew,
  llmAvailable = true,
  events = [],
  nearestUpcomingEvent = null,
  promptSuggestions = [],
  onFallbackToManual,
  timezone,
}) {
  const [stageIndex, setStageIndex] = useState(0);
  const textareaRef = useRef(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [showLongWaitNotice, setShowLongWaitNotice] = useState(false);

  // Rotate reasoning stages during analysis
  useEffect(() => {
    if (step !== 'analyzing') {
      setStageIndex(0);
      setShowLongWaitNotice(false);
      return;
    }

    const stageTimer = setInterval(() => {
      setStageIndex((prev) => (prev < REASONING_STAGES.length - 1 ? prev + 1 : prev));
    }, 1800);

    const longWaitTimer = setTimeout(() => {
      setShowLongWaitNotice(true);
    }, 5000);

    return () => {
      clearInterval(stageTimer);
      clearTimeout(longWaitTimer);
    };
  }, [step]);

  // Insert text at cursor or replace
  const insertTextAtCursor = (text) => {
    const input = textareaRef.current?.resizableTextArea?.textArea || textareaRef.current;
    if (input && typeof input.selectionStart === 'number') {
      const start = input.selectionStart;
      const end = input.selectionEnd;
      const current = prompt || '';
      const before = current.substring(0, start);
      const after = current.substring(end);
      const needLeadingSpace = before.length > 0 && !before.endsWith(' ');
      const needTrailingSpace = !after.startsWith(' ') && after.length > 0;
      const insertion = (needLeadingSpace ? ' ' : '') + text + (needTrailingSpace ? ' ' : ' ');
      const nextText = before + insertion + after;

      setPrompt(nextText);
      setTimeout(() => {
        const nextPos = start + insertion.length;
        input.focus();
        input.setSelectionRange(nextPos, nextPos);
      }, 0);
    } else {
      setPrompt((prev) => (prev ? `${prev} ${text}` : text));
    }
  };

  const speech = useSpeechInput({
    enabled: step === 'prompt',
    sessionKey: step,
    onResult: (transcript) => insertTextAtCursor(transcript),
  });

  // two characters is enough to send: "hi" and "help" are real first messages and get a reply that says what to ask
  const isTextValid = (prompt || '').trim().length >= 2;
  const handleBuild = async () => {
    try { if (isTextValid && onBuildCrew) await onBuildCrew(prompt); }
    catch { /* useAiCrew renders the request error; consume the event-handler rejection. */ }
  };

  const handleKeyDown = (e) => {
    if (e.isComposing || e.nativeEvent?.isComposing || e.keyCode === 229) return;
    // Enter sends like a chat; Shift+Enter adds a line (Ctrl/Cmd+Enter still works)
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent?.isComposing) {
      e.preventDefault();
      handleBuild();
    }
  };


  // 1. Loading State: Agent Reasoning HUD
  if (step === 'analyzing') {
    return (
      <div className="ai-crew-reasoning-hud" data-testid="ai-crew-loading-panel">
        <div className="ai-crew-hud-pulse-ring">
          <div className="ai-crew-hud-pulse-wave" />
          <div className="ai-crew-hud-pulse-core">
            <ThunderboltFilled />
          </div>
        </div>
        <div className="ai-crew-hud-title">CrewCall Agent Analyzing</div>
        <p style={{ color: '#64748b', fontSize: 14, margin: '0 0 16px 0' }}>
          Synthesizing request against labor rules and worker schedules
        </p>

        <div className="ai-crew-hud-stages">
          {REASONING_STAGES.map((stageText, idx) => {
            const isDone = idx < stageIndex;
            const isCurrent = idx === stageIndex;
            return (
              <div
                key={idx}
                className={`ai-crew-hud-stage-item ${isCurrent ? 'current' : ''} ${isDone ? 'done' : ''}`}
              >
                {isDone ? (
                  <CheckCircleFilled style={{ color: '#10b981', fontSize: 16 }} />
                ) : isCurrent ? (
                  <LoadingOutlined style={{ color: '#1677ff', fontSize: 16 }} spin />
                ) : (
                  <span style={{ width: 16, height: 16, borderRadius: '50%', border: '1px solid #cbd5e1' }} />
                )}
                <span>{stageText}</span>
              </div>
            );
          })}
        </div>
        {showLongWaitNotice && (
          <div
            className="ai-crew-hud-long-wait-notice"
            style={{ marginTop: 14, color: '#64748b', fontSize: 13, fontStyle: 'italic', textAlign: 'center' }}
          >
            Loading your employee list - first request can take ~30 seconds
          </div>
        )}
      </div>
    );
  }

  // 2. Manual Fallback Mode (when llmAvailable is false)
  if (!llmAvailable) {
    return (
      <div className="ai-crew-manual-panel" data-testid="ai-crew-manual-panel">
        <div className="ai-crew-prompt-title">Manual Shift Configuration</div>
        <div className="ai-crew-prompt-subtitle">
          Direct specification mode (AI language service unavailable)
        </div>
        <p>The language service is unavailable. Use the standard crew wizard to enter the shift details.</p>
        {onFallbackToManual && <Button type="primary" onClick={onFallbackToManual}>Open manual crew wizard</Button>}
      </div>
    );
  }

  // 3. Normal Single Text Box Mode
  return (
    <div className="ai-crew-prompt-panel" data-testid="ai-crew-prompt-panel">
      <div className="ai-crew-prompt-hero">
        <div className="ai-crew-prompt-title">Assemble Crew with AI</div>
        <div className="ai-crew-prompt-subtitle">
          Build a crew, check who is working, or ask about an employee’s schedule. Missing a detail? We’ll ask you for it before making any changes.
        </div>
      </div>

      <div className="ai-crew-prompt-card">
        {timezone && <p className="ai-crew-timezone-note">Schedule timezone: {timezone}</p>}
        <div className="ai-crew-textarea-wrap">
          <Input.TextArea
            aria-label="Your Crew Call request"
            ref={textareaRef}
            rows={3}
            autoFocus
            maxLength={MAX_PROMPT_LENGTH}
            showCount
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              speech.listening
                ? 'Listening...'
                : nearestUpcomingEvent
                  ? `e.g. 20 people tomorrow night for ${nearestUpcomingEvent.label}, 10pm to 6am`
                  : 'e.g. 20 people tomorrow night for an event move-in, 10pm to 6am'
            }
            className="ai-crew-textarea"
          />
          {speech.supported && (
            <button
              type="button"
              className={`ai-crew-mic-btn ${speech.listening ? 'ai-crew-mic-btn--active' : ''}`}
              onClick={() => (speech.listening ? speech.stop() : speech.start())}
              title={speech.listening ? 'Stop listening' : 'Speak your request'}
              aria-label={speech.listening ? 'Stop listening' : 'Speak your request'}
            >
              {speech.listening ? <AudioMutedOutlined /> : <AudioOutlined />}
            </button>
          )}
        </div>

        {/* Prompt Starters */}
        {promptSuggestions.length > 0 && (
          <div className="ai-crew-starters-section">
            <span className="ai-crew-starters-label">Try asking:</span>
            <div className="ai-crew-starters-list">
              {promptSuggestions.map((suggestion, idx) => (
                <button
                  key={idx}
                  className="ai-crew-starter-pill"
                  onClick={() => setPrompt(suggestion)}
                  type="button"
                >
                  <ThunderboltOutlined style={{ fontSize: 11, color: '#1677ff' }} />
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Upcoming Events Chips */}
        {events.length > 0 && (
          <div className="ai-crew-events-row">
            <span className="ai-crew-events-label">
              <CalendarOutlined /> Upcoming Events:
            </span>
            {events.map((e) => {
              // Highlight follows the chip the user actually clicked; before any click,
              // it defaults to the nearest upcoming event (not a permanent "nearest" badge --
              // clicking a different chip must move the highlight, not just insert text).
              const isHighlighted = selectedEventId ? selectedEventId === e.id : nearestUpcomingEvent?.id === e.id;
              return (
                <button
                  key={e.id}
                  className={isHighlighted ? 'ai-crew-event-chip ai-crew-event-chip--nearest' : 'ai-crew-event-chip'}
                  onClick={() => {
                    insertTextAtCursor(e.label);
                    setSelectedEventId(e.id);
                  }}
                  type="button"
                  title="Click to insert event name"
                >
                  {e.label}
                </button>
              );
            })}
          </div>
        )}

        <div className="ai-crew-submit-row">
          <div className="ai-crew-kbd-shortcut">
            <kbd>Enter</kbd>
            <span>to send,</span>
            <kbd>Shift + Enter</kbd>
            <span>for a new line</span>
          </div>

          <Button
            type="primary"
            size="large"
            disabled={!isTextValid}
            onClick={handleBuild}
            className="ai-crew-build-btn"
            icon={<ArrowRightOutlined />}
            iconPosition="end"
          >
            Send request
          </Button>
        </div>
      </div>
    </div>
  );
}
